/**
 * The ontology kinds a user may create through the GENERIC create path
 * (the "+ Create" flow that is not bound to a specific entity type).
 *
 * `principal`/`activity`/`event`/`default`/`person` are intentionally excluded —
 * those cover users, notes, meetings, enrichment requests, and external persons,
 * none of which are meant to be spawned from the generic create affordance.
 *
 * This is NOT the same set as `CREATE_FORM_DOMAIN_KINDS` in CrmCreateForm.tsx —
 * that set filters association fields and includes `principal` + `activity`.
 */
export const CREATABLE_ENTITY_KINDS: ReadonlySet<string> = new Set([
  'party',
  'case',
  'asset',
]);

/**
 * Whether `entityType` may be created through the generic create path.
 *
 * Entity types carry MULTIPLE kinds (e.g. `ahoy_contact` = [default, person, party]),
 * so this check is an INTERSECTION between the type's kinds and
 * `CREATABLE_ENTITY_KINDS` — true if any of the type's kinds is creatable.
 *
 * CUSTOM types (the `org_` prefix — the same marker `isValidEntityTypeName` uses)
 * bypass the allowlist entirely. The allowlist exists to withhold the generic
 * affordance from SYSTEM types that carry a non-creatable kind — notes and
 * meetings (`activity`), users (`principal`), external persons (`person`) — each
 * of which is created through its own bespoke flow. A custom type has no bespoke
 * flow: the generic modal is its ONLY create path, so gating it on kind makes it
 * permanently uncreatable from the UI. The object-type wizard now stamps
 * `['default', <chosen kind>]` on every wizard-created custom type
 * (`buildObjectTypeEvents`), but the bypass is still required for two separate
 * reasons:
 *   1. Types created before this change still carry `['default']` alone on
 *      disk — backfilling them is explicitly out of scope, so they must keep
 *      working under the old contract.
 *   2. Even under the new contract, a wizard-created type whose chosen kind is
 *      `activity` fails the allowlist check: `CREATABLE_ENTITY_KINDS` excludes
 *      `activity` because SYSTEM activity types (notes) have their own bespoke
 *      create flow — that exclusion is about the bespoke flow, not about
 *      whether the event contract applies to the custom type.
 * The real create gates still apply downstream: the schema's `user_creatable`
 * flag, the UI-create blocklist (`isUiCreatableEntityType`), and the per-user
 * `ahoy_object_created` permission.
 *
 * Fails open while `entityKindMap` is still loading (empty map): returns `true`
 * so create affordances never flicker hidden and valid creates are never refused
 * mid-load. Once loaded, an unknown entity type (no entry in a non-empty map)
 * returns `false`.
 */
export function isCreatableEntityKind(
  entityType: string,
  entityKindMap: ReadonlyMap<string, readonly string[]>
): boolean {
  if (entityKindMap.size === 0) {
    return true;
  }
  // `*_timeline` names alias an underlying type and are never object-type
  // identifiers in product surfaces (same guard as `isValidEntityTypeName`).
  if (entityType.startsWith('org_') && !entityType.endsWith('_timeline')) {
    return true;
  }
  const kinds = entityKindMap.get(entityType) ?? [];
  return kinds.some(k => CREATABLE_ENTITY_KINDS.has(k));
}
